/* global AbortController, setTimeout, clearTimeout */

import * as TaskManager from 'expo-task-manager';
import * as BackgroundFetch from 'expo-background-fetch';
import AsyncStorage from '@react-native-async-storage/async-storage';
import {getAccessToken, getUserInfo, setUserInfo} from './storage';
import {persistLastUser, slimLastUser} from './lastUserCache';
import {persistRestaurant} from './restaurantCache';
import {safeSetJson} from './storageQuota';
import {generateObjectId} from './objectId';
import {generateHappyId} from './happyId';
import {Platform} from 'react-native';
import ErrorReportingService from './ErrorReportingService';
import {normalizeOrderType} from './orderVatResolver';
import {collectAssignedTerminalProductIds, terminalEventPrintsProduct} from './printerProfiles';
import {printOrderLabels} from './printerLibrary/labelPrinter';
import {invalidateFoodLookupCache} from './lineItemHydration';
import i18n from '../../i18n';
import {serializeLastOrderResponse} from './orderHistorySlim';

const Toast = require('react-native-toast-message').default;

// Define task names
export const FETCH_SYSTEM_DATA_TASK = 'FETCH_SYSTEM_DATA_TASK';
export const FETCH_BOOKINGS_TASK = 'FETCH_BOOKINGS_TASK';
export const STORE_ORDER_TASK = 'STORE_ORDER_TASK';
export const PRINT_RECEIPT_TASK = 'PRINT_RECEIPT_TASK';
export const PRINT_KITCHEN_TASK = 'PRINT_KITCHEN_TASK';
export const FISCAL_PRINTER_TASK = 'FISCAL_PRINTER_TASK';
export const SEND_TO_CASHIER_TASK = 'SEND_TO_CASHIER_TASK';
export const PRINT_CASH_REGISTER_TASK = 'PRINT_CASH_REGISTER_TASK';

const CASHIER_CONNECT_TIMEOUT_MS = 15000;
const CASHIER_WRITE_TIMEOUT_MS = 30000;
const PRINTER_OPERATION_TIMEOUT_MS = 5000;

const withTimeout = (promise, timeoutMs, timeoutMessage) => {
  return Promise.race([
    promise,
    new Promise((_, reject) => {
      globalThis.setTimeout(() => reject(new Error(timeoutMessage)), timeoutMs);
    }),
  ]);
};

const hasPrinterTarget = (printer = {}) => {
  return Boolean(printer?.ip || printer?.address || printer?.macAddress);
};

const getPrinterTargetKey = (printer = {}) => {
  if (printer?.ip) {
    return `tcp:${printer.ip}:${Number(printer.port || 9100)}`;
  }

  const bluetoothAddress = printer?.address || printer?.macAddress;
  return bluetoothAddress ? `bt:${bluetoothAddress}` : '';
};

const dedupePrinterTargets = (printers = []) => {
  const uniquePrinters = [];
  const seen = new Set();

  printers.forEach((printer) => {
    if (!hasPrinterTarget(printer)) {
      return;
    }

    const key = getPrinterTargetKey(printer);
    if (!key || seen.has(key)) {
      return;
    }

    seen.add(key);
    uniquePrinters.push(printer);
  });

  return uniquePrinters;
};

const resolvePrintResultError = (result, fallbackMessage) => {
  if (result === true) {
    return null;
  }

  if (result?.error instanceof Error) {
    return result.error;
  }

  if (result && result.error) {
    return new Error(result.error?.message || fallbackMessage);
  }

  if (result === false || typeof result === 'undefined') {
    return new Error(fallbackMessage);
  }

  return null;
};

const printToTarget = async ({ sendPrinterAndroid, payload, printer, widthMM = 76, charactersPerLine = 48, showAlert = true }) => {
  let result;
  // Retries stop at the same deadline this call is raced against, so a timed-out
  // job never keeps hammering the printer while the next queued job prints.
  const printOptions = { deadlineAt: Date.now() + PRINTER_OPERATION_TIMEOUT_MS };

  if (printer?.ip) {
    result = await withTimeout(
      sendPrinterAndroid(payload, {
        ip: printer.ip,
        port: Number(printer.port),
        timeout: 400,
        autoCut: true,
        mmFeedPaper: 230,
        printerDpi: 203,
        printerWidthMM: widthMM,
        printerNbrCharactersPerLine: charactersPerLine,
      }, true, showAlert, printOptions),
      PRINTER_OPERATION_TIMEOUT_MS,
      `Printing timed out for ${getPrinterTargetKey(printer)}`
    );
  } else {
    result = await withTimeout(
      sendPrinterAndroid(payload, {
        macAddress: (printer.address || printer.macAddress)
      }, true, showAlert, printOptions),
      PRINTER_OPERATION_TIMEOUT_MS,
      `Printing timed out for ${getPrinterTargetKey(printer)}`
    );
  }

  const printError = resolvePrintResultError(result, `Printing failed for ${getPrinterTargetKey(printer) || 'configured printer'}`);
  if (printError) {
    throw printError;
  }

  return true;
};

// Defensive JSON parser for background fetches. The backend can answer with an
// HTML body instead of JSON (Cloudflare/maintenance interstitial, a proxy 502,
// an auth redirect, etc.) — sometimes even with a 200 status. Calling
// `response.json()` directly on that body throws
// `SyntaxError: JSON Parse error: Unexpected character: <` as an UNHANDLED
// promise rejection from these background tasks (Sentry HAPPY-IOS-5). We read the
// body as text first and parse it ourselves, returning a safe `fallback` instead
// of throwing so a transient non-JSON response can never crash the JS thread.
const parseJsonResponse = async (response, { fallback = null, label = '' } = {}) => {
  if (!response) return fallback;
  let text;
  try {
    text = await response.text();
  } catch (_error) {
    return fallback;
  }
  if (!text) return fallback;
  try {
    return JSON.parse(text);
  } catch (_error) {
    console.warn(
      `[BackgroundTasks] Expected JSON${label ? ` for ${label}` : ''} but got a non-JSON response`
      + ` (status ${response.status}): ${String(text).slice(0, 120)}`,
    );
    return fallback;
  }
};

const fetchJsonWithAuth = async (url, token, signal) => {
  const response = await fetch(url, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
    signal,
  });

  if (!response.ok) {
    throw new Error(`HTTP ${response.status} for ${url}`);
  }

  return parseJsonResponse(response, { fallback: [], label: url });
};

/**
 * Normalise a foods endpoint payload to `{ items, pages }`.
 *
 * `/foods/mobile` answers with a bare array, but the paginated `/foods`
 * fallback ALWAYS answers with `{ page, pages, from, to, count, data }` once a
 * page/limit param is present — treating that wrapper as "not an array" made
 * the whole fallback chain dead code (foods list empty even though the request
 * visibly returned data).
 *
 * @param {*} payload Parsed JSON body from a foods endpoint.
 * @returns {{ items: Array, pages: number }|null} `null` when the payload is
 *   neither a bare array nor a pagination wrapper.
 */
export const unwrapFoodsPayload = (payload) => {
  if (Array.isArray(payload)) {
    return { items: payload, pages: 1 };
  }
  if (payload && typeof payload === 'object' && Array.isArray(payload.data)) {
    return { items: payload.data, pages: Number(payload.pages) || 1 };
  }

  return null;
};

// Upper bound on pagination walking — at the deployed backend's ~100 items/page
// this allows catalogs up to ~3000 foods while bounding a pathological `pages`.
const MAX_FOOD_CATALOG_PAGES = 30;

/**
 * Fetch the full foods catalog, trying each candidate endpoint in order and
 * walking every page of the paginated fallbacks.
 *
 * @param {Object} params restaurantId/placeId/token/signal.
 * @returns {Promise<{items: Array, complete: boolean}>} `complete` is `false`
 *   when the page walk broke off early (transient page failure, HTML
 *   interstitial, abort) — callers must NOT let such a partial catalog
 *   overwrite a fuller cached one (see persistCatalogIfHealthy).
 */
export const fetchFoodsCatalogWithFallback = async ({ restaurantId, placeId, token, signal }) => {
  const encodedPlaceId = encodeURIComponent(placeId || '');
  const candidates = [
    `https://api.happysolutions.hu/api/restaurant/${restaurantId}/foods/mobile?placeId=${encodedPlaceId}&lowKey=true&beta=true&showDisabled=false`,
    `https://api.happysolutions.hu/api/restaurant/${restaurantId}/foods?placeId=${encodedPlaceId}&lowKey=false&beta=false&showDisabled=false&page=1&limit=500&basicDetails=true`,
    `https://sandbox.api.spirit.happysolutions.hu/api/restaurant/${restaurantId}/foods?placeId=${encodedPlaceId}&lowKey=false&beta=false&showDisabled=false&page=1&limit=500&basicDetails=true`,
  ];

  let bestPartial = null;

  for (const url of candidates) {
    try {
      const first = unwrapFoodsPayload(await fetchJsonWithAuth(url, token, signal));
      if (!first) {
        continue; // Mis-shaped payload — try the next candidate endpoint.
      }

      // Skip/limit pages over a live collection can shift boundaries mid-walk
      // (an admin creates/reorders a food) and repeat a document across two
      // consecutive pages — dedupe by _id so SaleScreen's FlatList never gets
      // duplicate keys and the cached catalog never holds double tiles.
      const seenIds = new Set();
      const items = [];
      const addItems = (list) => {
        for (const item of list) {
          const id = item && item._id ? String(item._id) : null;
          if (id) {
            if (seenIds.has(id)) {
              continue;
            }
            seenIds.add(id);
          }
          items.push(item);
        }
      };
      addItems(first.items);

      // The deployed backend caps the page size (limit=500 still yields ~100
      // items/page), so a 1300+ item catalog spans many pages — fetch them all.
      let complete = true;
      const totalPages = Math.min(first.pages, MAX_FOOD_CATALOG_PAGES);
      if (first.pages > MAX_FOOD_CATALOG_PAGES) {
        console.warn(`[BackgroundTasks] Foods catalog has ${first.pages} pages; only fetching the first ${MAX_FOOD_CATALOG_PAGES}`);
      }
      for (let page = 2; page <= totalPages; page++) {
        try {
          const next = unwrapFoodsPayload(await fetchJsonWithAuth(url.replace('page=1', `page=${page}`), token, signal));
          if (!next || next.items.length === 0) {
            // An empty/mis-shaped page before `pages` is a failure signal (5xx
            // degraded to [], HTML interstitial, abort) — not "end of data".
            complete = false;
            break;
          }
          addItems(next.items);
        } catch (_error) {
          complete = false;
          break; // Keep the pages fetched so far rather than dropping everything.
        }
      }

      if (items.length > 0 && complete) {
        return { items, complete: true };
      }
      if (items.length > 0 && (!bestPartial || items.length > bestPartial.length)) {
        bestPartial = items;
      }
    } catch (_error) {
      // Try next candidate endpoint.
    }
  }

  return { items: bestPartial || [], complete: false };
};

/**
 * Ingestion filter for the POS foods cache.
 *
 * Field semantics follow the backend Food schema defaults: `enabled` defaults
 * to true (missing flag keeps the food), while `visible` defaults to FALSE — a
 * food without a stored `visible: true` was never shown by the POS and must
 * stay hidden. The paginated `/foods` fallback also does NOT replicate
 * `/foods/mobile`'s server-side exclusions (ingredient rows, soft-deleted
 * "Törölt"/"Törölve" names), so those are enforced here for every source.
 *
 * @param {Object} food Raw food document from any foods endpoint.
 * @returns {boolean} Whether the food belongs in the POS sale catalog.
 */
export const isPosCatalogFood = (food) => {
  if (!food || food.enabled === false || food.visible !== true) {
    return false;
  }
  if (food.foodType === 'ingredient' || food.foodType === 'ingredients') {
    return false;
  }
  const huName = String(food.name?.hu || '').trim().toLowerCase();
  if (huName === 'törölt' || huName === 'törölve') {
    return false;
  }

  return true;
};

/**
 * Reduce a food document to the fields the POS actually renders, in a shape
 * every consumer can rely on:
 * - `category` normalised to a bare id — some endpoints populate it as a full
 *   object, which breaks string-id filters and balloons the persisted payload
 *   past the ~5MB web localStorage quota.
 * - `categories` persisted as a bare id-string array (when present) alongside
 *   `category`, so offline multi-category membership checks (foodCategoryIds)
 *   keep working without bloating the slim payload.
 * - sizes stripped of platform-integration/cost fields (priceSet/remoteCodes/
 *   remoteIds/purchase*) — unused by the app, and dropping them is what keeps a
 *   1000+ item catalog under the storage quota (QuotaExceededError).
 * - `visible`/`enabled` normalised to real booleans so SaleScreen's strict
 *   `=== true` checks can't silently drop foods when an endpoint omits them.
 *
 * @param {Object} food Raw food document from any foods endpoint.
 * @returns {Object} Slimmed food safe to persist in the AsyncStorage catalog.
 */
export const slimFoodForCache = (food) => ({
  _id: food._id,
  name: food.name,
  // Bare id-string array for multi-category membership; empty on legacy/
  // un-backfilled foods, callers fall back to `category` via foodCategoryIds.
  categories: Array.isArray(food.categories) ? food.categories.map((c) => (c && typeof c === 'object' ? String(c._id || c.id || c) : String(c))) : undefined,
  // Codes the sale screen searches by (name / barcode / SKU). Dropping them here
  // is what made an offline code search silently impossible.
  sku: food.sku,
  barcode: food.barcode,
  description: food.description,
  color: food.color,
  photo: food.photo,
  image: food.image,
  category: (food.category && typeof food.category === 'object') ? food.category._id : food.category,
  order: food.order,
  sizes: Array.isArray(food.sizes)
    ? food.sizes.map(({ priceSet, remoteCodes, remoteIds, purchasePrices, purchasePriceAvg, purchasePrice, ...size }) => size)
    : [],
  vat: food.vat || food.vats,
  visible: food.visible === true,
  enabled: food.enabled !== false,
  foodType: food.foodType,
  extras: food.extras,
  allergens: food.allergens
});

/**
 * Read a previously persisted catalog array from AsyncStorage.
 *
 * @param {string} key AsyncStorage key ('foods' | 'categories').
 * @returns {Promise<Array>} The cached array, or `[]` when absent/corrupt.
 */
const readCachedCatalog = async (key) => {
  try {
    const parsed = JSON.parse((await AsyncStorage.getItem(key)) || '[]');

    return Array.isArray(parsed) ? parsed : [];
  } catch (_error) {
    return [];
  }
};

/**
 * Persist a freshly-fetched catalog array to AsyncStorage, but NEVER clobber an
 * existing non-empty cache with an empty result, and never let an INCOMPLETE
 * fetch (a page walk that broke off early) shrink a fuller cache.
 *
 * A transient failure — expired/invalid token (a 401 on a bare `fetch` does not
 * reject, it degrades to a `[]` fallback), a network blip that fails every foods
 * endpoint, or a mis-shaped/non-JSON payload — can legitimately reduce a fetch to
 * `[]` or to a truncated first-few-pages list. Blindly persisting that empties or
 * guts the POS catalog the user is already looking at (foods/categories "show
 * for a while then vanish"). This mirrors the printers empty-guard in
 * SystemContext (`componentDidUpdate`), which foods and categories previously
 * lacked.
 *
 * @param {string} key AsyncStorage key to write ('foods' | 'categories').
 * @param {Array} nextArray Freshly fetched + filtered array to persist.
 * @param {Object} [options] Persistence options.
 * @param {boolean} [options.complete=true] Whether the fetch retrieved the full
 *   dataset. Partial results only persist when they are at least as large as
 *   the existing cache.
 * @returns {Promise<boolean>} `true` if the new value was written, `false` if an
 *   existing cache was preserved instead.
 */
export const persistCatalogIfHealthy = async (key, nextArray, { complete = true } = {}) => {
  const next = Array.isArray(nextArray) ? nextArray : [];

  if (next.length > 0) {
    if (!complete) {
      const existing = await readCachedCatalog(key);
      if (existing.length > next.length) {
        console.warn(`[BackgroundTasks] Refusing to overwrite cached "${key}" (${existing.length} items) with a partial fetch (${next.length} items)`);

        return false;
      }
    }

    // On web, AsyncStorage is backed by localStorage (~5MB) so a large catalog can
    // throw QuotaExceededError; on a full Android SQLite DB the write fails the same
    // way. `safeSetItem` evicts re-fetchable caches and retries, skips the write
    // entirely when the payload is byte-identical to the cached one (the common
    // case on a sync cycle — no multi-MB string handed to the storage engine on a
    // low-RAM terminal), and never rejects, so a failure here cannot abort the rest
    // of the system-data sync (user/vipCards/bookings/…).
    const { ok } = await safeSetJson(key, next, {
      label: `${key} (${next.length} items)`,
      // The catalog IS the thing being written — never let the reclaim pass evict
      // the sibling catalog to make room for it.
      maxTier: 3,
    });

    if (key === 'foods') {
      // Order sends hydrate from an in-memory lookup; make them see the new catalog.
      invalidateFoodLookupCache();
    }

    return ok;
  }

  const existing = await readCachedCatalog(key);
  if (existing.length > 0) {
    console.warn(`[BackgroundTasks] Refusing to overwrite cached "${key}" with an empty list — keeping ${existing.length} cached item(s)`);

    return false;
  }

  // No prior cache either — safe to persist the (empty) array.
  const { ok } = await safeSetJson(key, next, { label: key });

  return ok;
};

// Register background tasks
TaskManager.defineTask(FETCH_SYSTEM_DATA_TASK, async () => {
  try {
    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem('restaurant');
    const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');

    if (!token || !restaurantData || !restaurantPlaceData) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const parsedRestaurantData = JSON.parse(restaurantData);
    const parsedRestaurantPlaceData = JSON.parse(restaurantPlaceData);

    // Create an AbortController with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    // Declare response variables outside the try block
    let userResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse;
    let userData = {};
    let categoriesData = [];
    let vipCardsData = [];
    let bookingsData = [];
    let adminData = [];
    let foodsData = [];
    let foodsComplete = false;

    try {
      [userResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse] = await Promise.all([
        fetch('https://api.happysolutions.hu/api/user/me', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch(`https://api.happysolutions.hu/api/restaurant/${parsedRestaurantData._id}/categories?onlyVisible=true&beta=true`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch('https://api.happysolutions.hu/api/vipcard/list', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch('https://api.happysolutions.hu/api/booking/restaurant', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        }),
        fetch('https://api.happysolutions.hu/api/restaurant/admin', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        })
      ]);

      // Read the five bodies BEFORE the (potentially long) foods page walk: the
      // walk shares this AbortController, and an abort mid-walk also rejects any
      // still-unread body streams on web — parse first so user/vipCards/
      // bookings/admins survive a foods timeout.
      userData = await parseJsonResponse(userResponse, { fallback: {}, label: 'user/me' });
      categoriesData = await parseJsonResponse(categoriesResponse, { fallback: [], label: 'categories' });
      vipCardsData = await parseJsonResponse(vipCardsResponse, { fallback: [], label: 'vipcards' });
      bookingsData = await parseJsonResponse(bookingsResponse, { fallback: [], label: 'bookings' });
      adminData = await parseJsonResponse(adminResponse, { fallback: [], label: 'admin' });

      ({ items: foodsData, complete: foodsComplete } = await fetchFoodsCatalogWithFallback({
        restaurantId: parsedRestaurantData._id,
        placeId: parsedRestaurantPlaceData._id,
        token,
        signal: controller.signal,
      }));

      // Clear the timeout since all requests completed
      clearTimeout(timeoutId);
    } catch (error) {
      // Clear the timeout if there was an error
      clearTimeout(timeoutId);

      // Handle AbortError specifically
      if (error.name === 'AbortError') {
        console.error('Fetch requests timed out');
      }

      throw error;
    }

    // Filter and store categories with only needed properties
    const filteredCategories = categoriesData
      .filter(category => category.visible && category.enabled && !category.name.hu.toLowerCase().includes('törölt'))
      .map(category => ({
        _id: category._id,
        name: category.name,
        color: category.color,
        photo: category.photo,
        parent: category.parent,
        order: category.order,
        visible: category.visible,
        enabled: category.enabled
      }));
    await persistCatalogIfHealthy('categories', filteredCategories);

    // Filter to POS-sellable foods (backend visibility/enabled semantics plus
    // the exclusions /foods/mobile applies server-side but the fallback doesn't).
    const filteredFoods = foodsData
      .filter(isPosCatalogFood)
      .map(slimFoodForCache);

    const filteredAdmins = adminData.map(admin => ({
      _id: admin._id,
      name: admin.name,
      email: admin.email,
      roles: admin.roles,
      whereIs: admin.whereIs
    }))

    await setUserInfo(userData);
    // Slim projection + guarded write: the raw user payload crossed browser
    // localStorage quota on bloated accounts (Sentry HAPPY-APP-1D).
    await persistLastUser(userData);
    // Slim projection + quota-guarded write — the raw store document carries the
    // unbounded credit ledgers that crossed the browser localStorage quota, and
    // re-serializing it every sync cycle stalled low-RAM terminals even where it
    // fit. Identical payloads are skipped outright. See helpers/restaurantCache.
    await persistRestaurant(userData?.restaurants?.[0] || parsedRestaurantData);


    await persistCatalogIfHealthy('foods', filteredFoods, { complete: foodsComplete });

    await safeSetJson('vipCards', vipCardsData);
    await safeSetJson('bookings', bookingsData);
    await safeSetJson('admins', filteredAdmins);
    // Store other data

    // Set a flag to indicate data has been updated
    await AsyncStorage.setItem('systemDataUpdated', 'true');

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

TaskManager.defineTask(FETCH_BOOKINGS_TASK, async () => {
  try {
    const token = await getAccessToken();

    if (!token) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Create an AbortController with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    // Declare response variable
    let response;

    try {
      response = await fetch('https://api.happysolutions.hu/api/booking/restaurant', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        signal: controller.signal
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);
    } catch (error) {
      // Clear the timeout if there was an error
      clearTimeout(timeoutId);

      // Handle AbortError specifically
      if (error.name === 'AbortError') {
        console.error('Bookings fetch request timed out');
      }

      throw error;
    }

    const bookingsData = await parseJsonResponse(response, { fallback: [], label: 'booking/restaurant' });
    await safeSetJson('bookings', bookingsData);
    await AsyncStorage.setItem('bookingsUpdated', 'true');

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background bookings task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Register background fetch tasks
export const registerBackgroundTasks = async () => {
  // BackgroundFetch.registerTaskAsync is not available on web (PWA/browser).
  // Skip silently instead of surfacing a noisy error in the console.
  if (Platform.OS === 'web') return false;

  try {
    await BackgroundFetch.registerTaskAsync(FETCH_SYSTEM_DATA_TASK, {
      minimumInterval: 15 * 60, // 15 minutes
      stopOnTerminate: false,
      startOnBoot: true,
    });

    await BackgroundFetch.registerTaskAsync(FETCH_BOOKINGS_TASK, {
      minimumInterval: 5 * 60, // 5 minutes
      stopOnTerminate: false,
      startOnBoot: true,
    });

    // Register STORE_ORDER_TASK to ensure it's properly tracked
    await BackgroundFetch.registerTaskAsync(STORE_ORDER_TASK, {
      minimumInterval: 30, // 30 seconds
      stopOnTerminate: false,
      startOnBoot: true,
    });

    console.log('Background tasks registered successfully');
    return true;
  } catch (error) {
    console.error('Background task registration failed:', error);
    return false;
  }
};

// Manually trigger background tasks
export const triggerSystemDataFetch = async () => {
  try {
    await AsyncStorage.setItem('systemDataUpdated', 'false');

    // Instead of using TaskManager.executeTaskAsync which might fail silently,
    // directly execute the task's logic
    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem('restaurant');
    const restaurantPlaceData = await AsyncStorage.getItem('restaurantPlace');

    if (!token || !restaurantData || !restaurantPlaceData) {
      console.log('Missing required data for system data fetch');
      return false;
    }

    const parsedRestaurantData = JSON.parse(restaurantData);
    const parsedRestaurantPlaceData = JSON.parse(restaurantPlaceData);

    console.log('Fetching system data...');

    try {
      // Create an AbortController with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      // Declare response variables
      let userResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse;
      let userData = {};
      let categoriesData = [];
      let vipCardsData = [];
      let bookingsData = [];
      let adminData = [];
      let foodsData = [];
      let foodsComplete = false;

      try {
        [userResponse, categoriesResponse, vipCardsResponse, bookingsResponse, adminResponse] = await Promise.all([
          fetch('https://api.happysolutions.hu/api/user/me', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch(`https://api.happysolutions.hu/api/restaurant/${parsedRestaurantData._id}/categories?onlyVisible=true&beta=true`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch('https://api.happysolutions.hu/api/vipcard/list', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch('https://api.happysolutions.hu/api/booking/restaurant', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
          fetch('https://api.happysolutions.hu/api/restaurant/admin', {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
            signal: controller.signal
          }),
        ]);

        // Read the five bodies BEFORE the (potentially long) foods page walk:
        // the walk shares this AbortController, and an abort mid-walk also
        // rejects any still-unread body streams on web — parse first so
        // user/vipCards/bookings/admins survive a foods timeout.
        userData = await parseJsonResponse(userResponse, { fallback: {}, label: 'user/me' });
        categoriesData = await parseJsonResponse(categoriesResponse, { fallback: [], label: 'categories' });
        vipCardsData = await parseJsonResponse(vipCardsResponse, { fallback: [], label: 'vipcards' });
        bookingsData = await parseJsonResponse(bookingsResponse, { fallback: [], label: 'bookings' });
        adminData = await parseJsonResponse(adminResponse, { fallback: [], label: 'admin' });

        ({ items: foodsData, complete: foodsComplete } = await fetchFoodsCatalogWithFallback({
          restaurantId: parsedRestaurantData._id,
          placeId: parsedRestaurantPlaceData._id,
          token,
          signal: controller.signal,
        }));

        // Clear the timeout since all requests completed
        clearTimeout(timeoutId);
      } catch (error) {
        // Clear the timeout if there was an error
        clearTimeout(timeoutId);

        // Handle AbortError specifically
        if (error.name === 'AbortError') {
          console.error('System data fetch requests timed out');
          return false;
        }

        throw error;
      }

      // Filter and store categories with only needed properties
      const filteredCategories = categoriesData
        .filter(category => category.visible && category.enabled && !category.name.hu.toLowerCase().includes('törölt'))
        .map(category => ({
          _id: category._id,
          name: category.name,
          color: category.color,
          photo: category.photo,
          parent: category.parent,
          order: category.order,
          visible: category.visible,
          enabled: category.enabled
        }));
      await persistCatalogIfHealthy('categories', filteredCategories);

      // Filter to POS-sellable foods (backend visibility/enabled semantics plus
      // the exclusions /foods/mobile applies server-side but the fallback doesn't).
      const filteredFoods = foodsData
        .filter(isPosCatalogFood)
        .map(slimFoodForCache);
      await persistCatalogIfHealthy('foods', filteredFoods, { complete: foodsComplete });

      const filteredAdmins = adminData.map(admin => ({
        _id: admin._id,
        name: admin.name,
        email: admin.email,
        roles: admin.roles,
        whereIs: admin.whereIs
      }))
      // Store other data
      await setUserInfo(userData);
      // `user` is a legacy duplicate of the payload `HG_USER_INFO` already holds.
      // Its only reader (WhatsNewModal) reads `roles` and already falls back to
      // `lastUser`, so it gets the same slim projection instead of a second full
      // copy of a multi-MB user document.
      await safeSetJson('user', slimLastUser(userData), { label: 'user' });
      await safeSetJson('vipCards', vipCardsData);
      await safeSetJson('bookings', bookingsData);
      await safeSetJson('admins', filteredAdmins);

      // Set a flag to indicate data has been updated
      await AsyncStorage.setItem('systemDataUpdated', 'true');

      console.log('System data fetched and stored successfully');
      return true;
    } catch (fetchError) {
      console.error('Error fetching system data:', fetchError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger system data fetch:', error);
    return false;
  }
};

export const triggerBookingsFetch = async () => {
  try {
    await AsyncStorage.setItem('bookingsUpdated', 'false');

    // Direct implementation for all platforms

    // Direct implementation for web and other platforms, or as fallback for Android
    console.log(`Fetching bookings data directly on ${Platform.OS}...`);
    const token = await getAccessToken();

    if (!token) {
      console.log('Missing required data for bookings fetch');
      return false;
    }

    try {
      // Create an AbortController with a timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

      let response;

      try {
        response = await fetch('https://api.happysolutions.hu/api/booking/restaurant', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          signal: controller.signal
        });

        // Clear the timeout since the request completed
        clearTimeout(timeoutId);
      } catch (error) {
        // Clear the timeout if there was an error
        clearTimeout(timeoutId);

        // Handle AbortError specifically
        if (error.name === 'AbortError') {
          console.error('Direct bookings fetch request timed out');
          return false;
        }

        throw error;
      }

      const bookingsData = await parseJsonResponse(response, { fallback: [], label: 'booking/restaurant' });
      await safeSetJson('bookings', bookingsData);
      await AsyncStorage.setItem('bookingsUpdated', 'true');

      console.log('Bookings data fetched and stored successfully');
      return true;
    } catch (fetchError) {
      console.error('Error fetching bookings data:', fetchError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger bookings fetch:', error);
    return false;
  }
};

// Check if data has been updated
export const checkSystemDataUpdated = async () => {
  const updated = await AsyncStorage.getItem('systemDataUpdated');
  return updated === 'true';
};

export const checkBookingsUpdated = async () => {
  const updated = await AsyncStorage.getItem('bookingsUpdated');
  return updated === 'true';
};

// Define store order task
TaskManager.defineTask(STORE_ORDER_TASK, async ({ data }) => {
  try {
    // Check if this order has already been processed recently
    const lastOrderResponse = await AsyncStorage.getItem('lastOrderResponse');
    const lastProcessedTime = await AsyncStorage.getItem('lastOrderProcessedTime');

    if (lastOrderResponse && lastProcessedTime && data.orderId) {
      const lastOrder = JSON.parse(lastOrderResponse);
      const currentTime = new Date().getTime();
      const lastTime = parseInt(lastProcessedTime);

      // If the same order was processed in the last 5 minutes, don't process it again
      if (lastOrder &&
        lastOrder.generatedId === data.orderId &&
        (currentTime - lastTime) < 5 * 60 * 1000) {
        console.log('Order already processed recently by background task, skipping duplicate submission');
        return BackgroundFetch.BackgroundFetchResult.NoData;
      }
    }

    // Validate that the order has items
    const cartItems = data.cartItems || [];
    //const alreadySentCartItems = data.alreadySentCartItems || [];

    if (cartItems.length === 0 && alreadySentCartItems.length === 0) {
      console.error('Empty order detected, skipping database storage');
      ErrorReportingService.info("Empty order detected, skipping database storage",
        { orderId: data.orderId, orderCode: data.orderCode },
        "STORE_ORDER_TASK");
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Check if all items have zero price
    const hasNonZeroPrice = [...cartItems, ...alreadySentCartItems].some(item =>
      (item.remotePrice || item.price || 0) > 0
    );

    if (!hasNonZeroPrice) {
      console.error('Order with zero price detected, skipping database storage');
      ErrorReportingService.info("Order with zero price detected, skipping database storage",
        { orderId: data.orderId, orderCode: data.orderCode, itemCount: cartItems.length + alreadySentCartItems.length },
        "STORE_ORDER_TASK");
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Set current time as the processing time for this order
    await AsyncStorage.setItem('lastOrderProcessedTime', new Date().getTime().toString());


    const {
      cartTotal,
      paymentType,
      paidAmount,
      discountsForWholeCart,
      vatInvoiceData,
      needInvoice,
      currentTable,
      serviceFeeAmount,
      isSavedForLater,
      alreadySentCartItems,
      orderId,
      paymentTypes,
      tip,
      originalAmount,
      selectedVipCard,
      userData,
      selectedItemsToPay,
      order,
      proform
    } = data;

    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem("restaurant");
    const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");

    if (!token || !restaurantData || !restaurantPlaceData) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const restaurant = JSON.parse(restaurantData);
    const restaurantPlace = JSON.parse(restaurantPlaceData);

    const API_URL = 'https://api.happysolutions.hu/api';
    const orderCode = data._stableOrderCode || generateHappyId();

    const convertPaymentType = () => {
      if (paymentType === "Készpénz") return "cash";
      if (paymentType === "Bankkártya") return "creditCard";
      if (paymentType === "Utalvány") return "voucher";
      if (paymentType === "VIP") return "vipBalance";
      if (paymentType === "Személyzeti") return "employeeBalance";
      return "temporary";
    };

    const itemsArrayforOrder = [
      ...(cartItems ?? []).map(item => {
        return {
          "item": item.id,
          "sizeIndex": item.sizes[item?.sizeIndex || 0]._id,
          "size": item.sizes[item?.sizeIndex || 0]._id,
          "quantity": (item.amount || item.quantity || 1),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 1),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      }),
      ...(alreadySentCartItems ?? []).map(item => {
        return {
          "item": item.item._id,
          "sizeIndex": item.size,
          "size": item.size,
          "quantity": (item.amount || item.quantity || 0),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 0),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      })
    ];

    const invoiceData = vatInvoiceData ? {
      "company": {
        "addressData": {
          "fullAddress": vatInvoiceData?.fullAddress || "",
          "city": vatInvoiceData?.city || "",
          "postCode": vatInvoiceData?.postCode || "",
          "street": vatInvoiceData?.address || "",
          "streetType": vatInvoiceData?.streetType || "",
          "houseNumber": vatInvoiceData?.houseNumber || "",
        },
        "nev": vatInvoiceData?.name || "",
        "adoszam": vatInvoiceData?.taxRefNum || "",
        "cim": vatInvoiceData?.fullAddress || "",
        "cegj_sz": vatInvoiceData?.cegj_sz || "",
        "arbev": vatInvoiceData?.arbev || "",
      },
      "firstName": vatInvoiceData?.firstName || "",
      "lastName": vatInvoiceData?.lastName || "",
      "email": vatInvoiceData?.email || "",
      "phone": vatInvoiceData?.phone || "",
      "comment": vatInvoiceData?.comment || "",
    } : null;

    const orderType = (isSavedForLater || selectedItemsToPay?.length > 0 || currentTable ? "dineIn" : normalizeOrderType(userData?.orderType, "takeAway"));
    const currentPaymentType = convertPaymentType();
    const bodyPaymentTypes = paymentTypes && paymentTypes.length > 0 ? paymentTypes : [
      {
        "paymentType": convertPaymentType(),
        "amount": paidAmount || cartTotal || 0,
        "change": 1,
        "currency": "HUF",
      }
    ];
    const selectedItems = selectedItemsToPay ? ({ "selectedItems": selectedItemsToPay }) : {};

    const bodyData = {
      "VIPCard": userData ? null : (selectedVipCard ? selectedVipCard?._id : null),
      "generatedId": data._stableGeneratedId || (orderType === "takeAway" ? generateObjectId() : (orderId ? orderId : generateObjectId())),
      "orderCode": orderCode,
      "restaurant": restaurant._id,
      "addressInfo": userData?.addressInfo || {},
      "userInfo": userData?.userInfo || {},
      "placeId": restaurantPlace._id,
      "preOrder": userData?.preOrder ? userData?.preOrder : null,
      "orderType": orderType,
      "serviceFee": currentTable?._id && restaurantPlace?.serviceFee || 0,
      "deliveryFee": 0,
      "editOrderSum": alreadySentCartItems && alreadySentCartItems.length > 0 ? alreadySentCartItems?.reduce((sum, item) => sum + (item.remotePrice || item.price) * (item.amount || item.quantity || 1), 0) : undefined,
      "serviceFeePrice": currentTable?._id && serviceFeeAmount || 0,
      "serviceFeeName": "",
      "orderFrom": restaurantPlace._id,
      "selectedPortal": userData?.selectedPortal || restaurantPlace._id,
      "proforma": proform || false,
      "tableId": currentTable?._id || "",
      "tableName": currentTable?.name || "",
      "tipAmount": tip || 0,
      "comment": "",
      ...selectedItems,
      "discounts": discountsForWholeCart,
      "paymentType": paymentTypes && paymentTypes.length > 0 ? paymentTypes[0].paymentType : currentPaymentType,
      "paymentTypes": isSavedForLater ? [] : bodyPaymentTypes,
      "items": [...itemsArrayforOrder],
      "originalItems": [...itemsArrayforOrder],
      "cartItems": [],
      "needInvoice": needInvoice || false,
    };

    if (isSavedForLater) {
      bodyData["isPayed"] = false;
    }

    const requestBody = invoiceData
      ? { ...bodyData, invoiceData }
      : bodyData;

    // Create an AbortController with a timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000); // 30 second timeout

    // Declare response variable outside the try block
    let response;

    try {
      response = await fetch(`${API_URL}/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestBody),
        signal: controller.signal
      });

      // Clear the timeout since the request completed
      clearTimeout(timeoutId);
    } catch (error) {
      // Clear the timeout if there was an error
      clearTimeout(timeoutId);

      // Handle AbortError specifically
      if (error.name === 'AbortError') {
        console.error('Order creation request timed out');
      }

      throw error;
    }

    const responseData = await parseJsonResponse(response, { fallback: null, label: 'order/create' });

    // Store the order response
    await AsyncStorage.setItem('lastOrderResponse', serializeLastOrderResponse(responseData));
    await AsyncStorage.setItem('orderStored', 'true');

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background store order task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger store order task
export const triggerStoreOrder = async (orderData) => {
  try {
    // Check if this order has already been processed recently
    const lastOrderResponse = await AsyncStorage.getItem('lastOrderResponse');
    const lastProcessedTime = await AsyncStorage.getItem('lastOrderProcessedTime');

    if (lastOrderResponse && lastProcessedTime) {
      const lastOrder = JSON.parse(lastOrderResponse);
      const currentTime = new Date().getTime();
      const lastTime = parseInt(lastProcessedTime);

      // If the same order was processed in the last 5 minutes, don't process it again
      if (lastOrder &&
        orderData.orderId &&
        lastOrder.generatedId === orderData.orderId &&
        (currentTime - lastTime) < 5 * 60 * 1000) {
        console.log('Order already processed recently, skipping duplicate submission');
        return true;
      }
    }

    // Set current time as the processing time for this order
    await AsyncStorage.setItem('lastOrderProcessedTime', new Date().getTime().toString());
    await AsyncStorage.setItem('orderStored', 'false');

    // Instead of using TaskManager.executeTaskAsync which might fail silently,
    // directly execute the task's logic
    const {
      cartTotal,
      cartItems,
      paymentType,
      paidAmount,
      discountsForWholeCart,
      vatInvoiceData,
      needInvoice,
      currentTable,
      serviceFeeAmount,
      isSavedForLater,
      alreadySentCartItems,
      orderId,
      paymentTypes,
      tip,
      originalAmount,
      selectedVipCard,
      userData,
      selectedItemsToPay,
      order,
      proform
    } = orderData;

    const token = await getAccessToken();
    const restaurantData = await AsyncStorage.getItem("restaurant");
    const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");

    if (!token || !restaurantData || !restaurantPlaceData) {
      console.log('Missing required data for store order');
      return false;
    }

    const restaurant = JSON.parse(restaurantData);
    const restaurantPlace = JSON.parse(restaurantPlaceData);

    const API_URL = 'https://api.happysolutions.hu/api';
    const orderCode = orderData._stableOrderCode || generateHappyId();

    const convertPaymentType = () => {
      if (paymentType === "Készpénz") return "cash";
      if (paymentType === "Bankkártya") return "creditCard";
      if (paymentType === "Utalvány") return "voucher";
      if (paymentType === "VIP") return "vipBalance";
      if (paymentType === "Személyzeti") return "employeeBalance";
      return "temporary";
    };
    const itemsArrayforOrder = [
      ...(cartItems ?? []).map(item => {

        return {
          "item": item.itemId,
          "sizeIndex": item?.sizeIndex || 0,
          "size": item.sizes[item?.sizeIndex || 0]._id,
          "quantity": (item.amount || item.quantity || 1),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 1),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      }),
      ...(alreadySentCartItems ?? []).map(item => {
        return {
          "item": item.item._id,
          "sizeIndex": item.size,
          "size": item.size,
          "quantity": (item.amount || item.quantity || 0),
          "extras": item.extras || [],
          "serveType": item.serveType || "",
          "comments": item.comments || [],
          "discounts": item.discounts || {},
          "round": item.round || 0,
          "amount": (item.amount || item.quantity || 0),
          "price": item.price,
          "remotePrice": item.remotePrice || item.price,
        };
      })
    ];

    const invoiceData = vatInvoiceData ? {
      "company": {
        "addressData": {
          "fullAddress": vatInvoiceData?.fullAddress || "",
          "city": vatInvoiceData?.city || "",
          "postCode": vatInvoiceData?.postCode || "",
          "street": vatInvoiceData?.address || "",
          "streetType": vatInvoiceData?.streetType || "",
          "houseNumber": vatInvoiceData?.houseNumber || "",
        },
        "nev": vatInvoiceData?.name || "",
        "adoszam": vatInvoiceData?.taxRefNum || "",
        "cim": vatInvoiceData?.fullAddress || "",
        "cegj_sz": vatInvoiceData?.cegj_sz || "",
        "arbev": vatInvoiceData?.arbev || "",
      },
      "firstName": vatInvoiceData?.firstName || "",
      "lastName": vatInvoiceData?.lastName || "",
      "email": vatInvoiceData?.email || "",
      "phone": vatInvoiceData?.phone || "",
      "comment": vatInvoiceData?.comment || "",
    } : null;

    const orderType = (isSavedForLater || selectedItemsToPay?.length > 0 || currentTable ? "dineIn" : normalizeOrderType(userData?.orderType, "takeAway"));
    const currentPaymentType = convertPaymentType();
    const bodyPaymentTypes = paymentTypes && paymentTypes.length > 0 ? paymentTypes : [
      {
        "paymentType": convertPaymentType(),
        "amount": paidAmount || cartTotal || 0,
        "change": 1,
        "currency": "HUF",
      }
    ];
    const selectedItems = selectedItemsToPay ? ({ "selectedItems": selectedItemsToPay }) : {};

    const bodyData = {
      "VIPCard": userData ? null : (selectedVipCard ? selectedVipCard?._id : null),
      "generatedId": orderData._stableGeneratedId || (orderType === "takeAway" ? generateObjectId() : (orderId ? orderId : generateObjectId())),
      "orderCode": orderCode,
      "restaurant": restaurant._id,
      "addressInfo": userData?.addressInfo || {},
      "userInfo": userData?.userInfo || {},
      "placeId": restaurantPlace._id,
      "preOrder": userData?.preOrder ? userData?.preOrder : null,
      "orderType": orderType,
      "serviceFee": currentTable?._id && restaurantPlace?.serviceFee || 0,
      "deliveryFee": 0,
      "editOrderSum": alreadySentCartItems && alreadySentCartItems.length > 0 ? alreadySentCartItems?.reduce((sum, item) => sum + (item.remotePrice || item.price) * (item.amount || item.quantity || 1), 0) : undefined,
      "serviceFeePrice": currentTable?._id && serviceFeeAmount || 0,
      "serviceFeeName": "",
      "orderFrom": restaurantPlace._id,
      "selectedPortal": userData?.selectedPortal || restaurantPlace._id,
      "proforma": proform || false,
      "tableId": currentTable?._id || "",
      "tableName": currentTable?.name || "",
      "tipAmount": tip || 0,
      "comment": "",
      ...selectedItems,
      "discounts": discountsForWholeCart,
      "paymentType": paymentTypes && paymentTypes.length > 0 ? paymentTypes[0].paymentType : currentPaymentType,
      "paymentTypes": isSavedForLater ? [] : bodyPaymentTypes,
      "items": [...itemsArrayforOrder],
      "originalItems": [...itemsArrayforOrder],
      "cartItems": [],
      "needInvoice": needInvoice || false,
    };

    if (isSavedForLater) {
      bodyData["isPayed"] = false;
    }

    const requestBody = invoiceData
      ? { ...bodyData, invoiceData }
      : bodyData;

    console.log('Sending order to API...');

    try {
      const response = await fetch(`${API_URL}/order/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(requestBody),
      });

      const responseData = await parseJsonResponse(response, { fallback: null, label: 'order/create' });

      // Store the order response
      await AsyncStorage.setItem('lastOrderResponse', serializeLastOrderResponse(responseData));
      await AsyncStorage.setItem('orderStored', 'true');

      console.log('Order stored successfully');
      return true;
    } catch (apiError) {
      console.error('API error when storing order:', apiError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger store order:', error);
    return false;
  }
};

// Check if order has been stored
export const checkOrderStored = async () => {
  const stored = await AsyncStorage.getItem('orderStored');
  return stored === 'true';
};

// Get the last order response
export const getLastOrderResponse = async () => {
  const response = await AsyncStorage.getItem('lastOrderResponse');
  return response ? JSON.parse(response) : null;
};

// Define receipt printing task
TaskManager.defineTask(PRINT_RECEIPT_TASK, async ({ data }) => {
  try {
    const { order } = data;

    if (!order) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Import Toast for notifications
    const Toast = require('react-native-toast-message').default;

    // Show printing in progress notification
    Toast.show({
      type: 'info',
      position: 'bottom',
      text1: i18n.t('printing_receipt_progress'),
      visibilityTime: 10000, // 10 seconds or until dismissed
    });

    const userInfo = await getUserInfo();
    const printerDevice = await AsyncStorage.getItem('printerDevice');
    const restaurantData = await AsyncStorage.getItem("restaurant");
    const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
    let printerData = printerDevice ? JSON.parse(printerDevice) : null;

    // Import required functions
    const { sendPrinterAndroid } = require('./printerLibrary/printer');
    const { getBigReceiptData } = require('./printerLibrary/oldPrinter');

    // Print to default printer if available
    if (printerData && (printerData.address || printerData.macAddress)) {
      await sendPrinterAndroid(
        getBigReceiptData(
          order,
          JSON.parse(restaurantData),
          JSON.parse(restaurantPlaceData),
          userInfo
        ),
        {
          ...printerData,
          macAddress: (printerData.address || printerData.macAddress)
        }
      );
    }

    // Print to receipt printers
    const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
    const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

    if (printerEvents.length > 0) {
      const event = printerEvents.find(event => event.type === "receipt");
      if (event && event.printers && event.printers.length > 0) {
        for (let i = 0; i < event.printers.length; i++) {
          const printer = event.printers[i];
          if (printer.ip) {
            await sendPrinterAndroid(
              getBigReceiptData(
                order,
                JSON.parse(restaurantData),
                JSON.parse(restaurantPlaceData),
                userInfo
              ),
              {
                ip: printer.ip,
                port: Number(printer.port),
                timeout: 400,
                autoCut: true,
                mmFeedPaper: 230,
                printerDpi: 203,
                // Use a slightly narrower printable area for 80mm paper to prevent edge wrapping.
                printerWidthMM: 72,
                printerNbrCharactersPerLine: 42,
              }
            );
          } else if (printer.address || printer.macAddress) {
            await sendPrinterAndroid(
              getBigReceiptData(
                order,
                JSON.parse(restaurantData),
                JSON.parse(restaurantPlaceData),
                userInfo
              ),
              {
                macAddress: (printer.address || printer.macAddress)
              }
            );
          }
          // Add a small delay between prints
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }
    }

    await AsyncStorage.setItem('receiptPrinted', 'true');

    // Hide the notification when printing is complete
    Toast.hide();

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background receipt printing task error:", error);

    // Hide the notification and show error if printing fails
    Toast.hide();
    Toast.show({
      type: 'error',
      position: 'bottom',
      text1: i18n.t('receipt_printing_failed'),
      text2: error.message,
      visibilityTime: 3000,
    });

    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger receipt printing task
export const triggerReceiptPrinting = async (order, options = {}) => {
  try {
    // printedTargets: optional Set shared across retries of the same job.
    const { showAlert = true, printedTargets = null } = options;
    await AsyncStorage.setItem('receiptPrinted', 'false');

    // Execute the task directly
    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return false;

    const { sendPrinterAndroid, showPrintErrorDialog } = require('./printerLibrary/printer');
    const { getBigReceiptData } = require('./printerLibrary/oldPrinter');

    try {
      // Direct implementation for receipt printing
      const [
        userInfo,
        printerDevice,
        restaurantData,
        restaurantPlaceData,
        storedPrinterEvents,
      ] = await Promise.all([
        getUserInfo(),
        AsyncStorage.getItem('printerDevice'),
        AsyncStorage.getItem("restaurant"),
        AsyncStorage.getItem("restaurantPlace"),
        AsyncStorage.getItem('printerEvents'),
      ]);
      let printerData = printerDevice ? JSON.parse(printerDevice) : null;
      const parsedRestaurantData = restaurantData ? JSON.parse(restaurantData) : {};
      const parsedRestaurantPlaceData = restaurantPlaceData ? JSON.parse(restaurantPlaceData) : {};
      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];
      const receiptEvent = printerEvents.find(event => event.type === "receipt");
      const receiptPayload = getBigReceiptData(
        order,
        parsedRestaurantData,
        parsedRestaurantPlaceData,
        userInfo
      );
      const receiptPrinters = dedupePrinterTargets([
        printerData,
        ...(Array.isArray(receiptEvent?.printers) ? receiptEvent.printers : []),
      ]);

      if (!receiptPrinters.length) {
        throw new Error('No receipt printers configured');
      }

      // Try EVERY printer (one dead printer must not starve the others) and
      // skip targets this job already printed on, so a retry only reprints the
      // printers that failed (see `options.printedTargets`).
      let failedCount = 0;
      for (let i = 0; i < receiptPrinters.length; i++) {
        const targetKey = `receipt:${getPrinterTargetKey(receiptPrinters[i])}`;
        if (printedTargets?.has(targetKey)) {
          continue;
        }
        try {
          await printToTarget({
            sendPrinterAndroid,
            payload: receiptPayload,
            printer: receiptPrinters[i],
            widthMM: 72,
            charactersPerLine: 42,
            showAlert,
          });
          printedTargets?.add(targetKey);
        } catch (targetError) {
          failedCount += 1;
          console.error('Receipt printer failed:', targetKey, targetError?.message || targetError);
        }
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      if (failedCount > 0) {
        return false;
      }

      await AsyncStorage.setItem('receiptPrinted', 'true');
      return true;
    } catch (printError) {
      console.error('Failed to print receipt directly:', printError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger receipt printing:', error);
    return false;
  }
};

// Check if receipt has been printed
export const checkReceiptPrinted = async () => {
  const printed = await AsyncStorage.getItem('receiptPrinted');
  return printed === 'true';
};

// Trigger send to cashier task
export const triggerSendToCashier = async (qrCodeString, options = {}) => {
  if (!qrCodeString) {
    console.error('No QR code string provided for cashier');
    return false;
  }

  // Fall back to direct implementation
  const { Platform } = require('react-native');
  if (Platform.OS !== 'android') return false;

  const BluetoothClassic = require('react-native-bluetooth-classic').default;
  const AsyncStorage = require('@react-native-async-storage/async-storage').default;

  const {
    attempts = 2,
    initialDelayMs = 500,
    maxDelayMs = 1500,
    cancellationToken,
  } = options || {};

  const isCancelled = () => Boolean(cancellationToken && cancellationToken.cancelled);

  const parseStoredDevice = (rawDevice) => {
    if (!rawDevice) {
      return null;
    }

    if (typeof rawDevice === 'object') {
      return rawDevice;
    }

    try {
      return JSON.parse(rawDevice);
    } catch (_error) {
      // Backward compatibility: some installs may store plain address strings.
      return { address: String(rawDevice) };
    }
  };

  let storedDeviceData = null;
  try {
    const storedDevice = await AsyncStorage.getItem('cashierDevice');
    storedDeviceData = parseStoredDevice(storedDevice);
  } catch (storageError) {
    return {
      error: storageError,
      stage: 'storage',
      attempt: 0,
      errorMessage: String(storageError?.message || storageError),
    };
  }

  if (!storedDeviceData || !storedDeviceData.address) {
    return false;
  }

  const deviceAddress = storedDeviceData.address;

  const safeDisconnect = async () => {
    if (typeof BluetoothClassic.disconnectFromDevice === 'function') {
      try {
        await BluetoothClassic.disconnectFromDevice(deviceAddress);
      } catch (_error) { /* ignore – device may not be connected */ }
    }
  };

  const totalAttempts = Math.max(1, Number(attempts) || 1);
  let lastError = null;
  let lastStage = 'connect';

  for (let attempt = 1; attempt <= totalAttempts; attempt += 1) {
    if (isCancelled()) {
      return {
        error: new Error('Cashier send cancelled by user'),
        stage: 'cancelled',
        attempt,
        errorMessage: 'Cancelled',
      };
    }

    try {
      // Always start each attempt from a clean slate – some BT stacks
      // refuse to re-use a half-open connection from the previous try.
      await safeDisconnect();

      await withTimeout(
        BluetoothClassic.connectToDevice(deviceAddress),
        CASHIER_CONNECT_TIMEOUT_MS,
        'Cashier Bluetooth connect timed out'
      );

      lastStage = 'write';
      await withTimeout(
        BluetoothClassic.writeToDevice(deviceAddress, qrCodeString),
        CASHIER_WRITE_TIMEOUT_MS,
        'Cashier Bluetooth write timed out'
      );

      // Best-effort disconnect so the next call starts cleanly too.
      await safeDisconnect();
      return true;
    } catch (error) {
      lastError = error;
      lastStage = String(error?.message || '').toLowerCase().includes('connect') ? 'connect' : lastStage;
      console.warn(`[triggerSendToCashier] attempt ${attempt}/${totalAttempts} failed (${lastStage}):`, error?.message || error);

      // Disconnect before next loop iteration.
      await safeDisconnect();

      if (attempt < totalAttempts && !isCancelled()) {
        const delay = Math.min(maxDelayMs, initialDelayMs * (2 ** (attempt - 1)));
        await new Promise(resolve => globalThis.setTimeout(resolve, delay));
      }
    }
  }

  return {
    error: lastError || new Error('Cashier send failed'),
    qrCodeString,
    stage: lastStage,
    attempt: totalAttempts,
    errorMessage: String(lastError?.message || lastError || 'Unknown cashier error'),
  };
};

// Trigger cash register receipt printing task
export const triggerPrintCashRegister = async (order) => {
  if (!order) {
    console.error('No order provided for cash register receipt');
    return false;
  }

  // When the customer requested a VAT invoice the order is billed via a NAV
  // invoice — no cash register receipt must be produced for it.
  try {
    const { orderNeedsInvoice } = require('./invoiceDetails');
    if (orderNeedsInvoice(order)) {
      console.log('Skipping cash register receipt — order requires a VAT invoice');
      return { skipped: true, reason: 'invoice_requested' };
    }
  } catch (_) { /* helper missing — fall through */ }

  try {
    // Direct implementation for cash register printing
    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return false;

    const { sendPrinterAndroid, showPrintErrorDialog } = require('./printerLibrary/printer');
    const { getCashRegisterReceiptData } = require('./printerLibrary/oldPrinter');

    try {
      const [
        userInfo,
        printerDevice,
        restaurantData,
        restaurantPlaceData,
        storedPrinterEvents,
      ] = await Promise.all([
        getUserInfo(),
        AsyncStorage.getItem('printerDevice'),
        AsyncStorage.getItem("restaurant"),
        AsyncStorage.getItem("restaurantPlace"),
        AsyncStorage.getItem('printerEvents'),
      ]);
      let printerData = printerDevice ? JSON.parse(printerDevice) : null;
      const parsedRestaurantData = restaurantData ? JSON.parse(restaurantData) : {};
      const parsedRestaurantPlaceData = restaurantPlaceData ? JSON.parse(restaurantPlaceData) : {};
      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];
      const cashRegisterEvent = printerEvents.find(event => event.type === "cashRegister");
      const cashRegisterPayload = getCashRegisterReceiptData(
        order,
        parsedRestaurantData,
        parsedRestaurantPlaceData,
        userInfo
      );
      const cashRegisterPrinters = dedupePrinterTargets([
        printerData,
        ...(Array.isArray(cashRegisterEvent?.printers) ? cashRegisterEvent.printers : []),
      ]);

      for (let i = 0; i < cashRegisterPrinters.length; i++) {
        await printToTarget({
          sendPrinterAndroid,
          payload: cashRegisterPayload,
          printer: cashRegisterPrinters[i],
          widthMM: 76,
          charactersPerLine: 48,
        });
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      return true;
    } catch (error) {
      console.error('Failed to print cash register receipt directly:', error);
      return { error, order };
    }
  } catch (outerError) {
    console.error('Failed to trigger cash register printing:', outerError);
    return false;
  }
};

// Define send to cashier task
TaskManager.defineTask(SEND_TO_CASHIER_TASK, async ({ data }) => {
  try {
    const { qrCodeString } = data;

    if (!qrCodeString) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return BackgroundFetch.BackgroundFetchResult.NoData;

    const BluetoothClassic = require('react-native-bluetooth-classic').default;
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    try {
      const storedDevice = await AsyncStorage.getItem('cashierDevice');
      const storedDeviceData = JSON.parse(storedDevice) || storedDevice;

      if (!storedDeviceData || !storedDeviceData.address) {
        return BackgroundFetch.BackgroundFetchResult.Failed;
      }

      const deviceAddress = storedDeviceData.address;

      try {
        await withTimeout(
          BluetoothClassic.connectToDevice(deviceAddress),
          CASHIER_CONNECT_TIMEOUT_MS,
          'Cashier Bluetooth connect timed out'
        );
        await withTimeout(
          BluetoothClassic.writeToDevice(deviceAddress, qrCodeString),
          CASHIER_WRITE_TIMEOUT_MS,
          'Cashier Bluetooth write timed out'
        );
        return BackgroundFetch.BackgroundFetchResult.NewData;
      } catch (error) {
        ErrorReportingService.error('Failed to send data to cashier', { error }, 'SEND_TO_CASHIER_TASK');
        ErrorReportingService.reportError(error, { source: 'SEND_TO_CASHIER_TASK', deviceAddress });
        return BackgroundFetch.BackgroundFetchResult.Failed;
      }
    } catch (error) {
      ErrorReportingService.error('Failed to get cashier device', { error }, 'SEND_TO_CASHIER_TASK');
      ErrorReportingService.reportError(error, { source: 'SEND_TO_CASHIER_TASK' });
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  } catch (error) {
    ErrorReportingService.error('Error in SEND_TO_CASHIER_TASK', { error }, 'SEND_TO_CASHIER_TASK');
    ErrorReportingService.reportError(error, { source: 'SEND_TO_CASHIER_TASK', task: 'main' });
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});


// Define cash register receipt task
TaskManager.defineTask(PRINT_CASH_REGISTER_TASK, async ({ data }) => {
  try {
    const { order } = data;

    if (!order) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return BackgroundFetch.BackgroundFetchResult.NoData;

    const { sendPrinterAndroid } = require('./printerLibrary/printer');
    const { getCashRegisterReceiptData } = require('./printerLibrary/oldPrinter');
    const AsyncStorage = require('@react-native-async-storage/async-storage').default;
    const { getUserInfo } = require('./storage');

    try {
      const userInfo = await getUserInfo();
      const printerDevice = await AsyncStorage.getItem('printerDevice');
      const restaurantData = await AsyncStorage.getItem("restaurant");
      const restaurantPlaceData = await AsyncStorage.getItem("restaurantPlace");
      let printerData = printerDevice ? JSON.parse(printerDevice) : null;

      if (printerData && (printerData.address || printerData.macAddress)) {
        await sendPrinterAndroid(
          getCashRegisterReceiptData(
            order,
            JSON.parse(restaurantData),
            JSON.parse(restaurantPlaceData),
            userInfo
          ),
          {
            ...printerData,
            macAddress: (printerData.address || printerData.macAddress)
          },
          true,
          false
        );
      }

      const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

      if (printerEvents.length > 0) {
        const event = printerEvents.find(event => event.type === "cashRegister");
        if (event && event.printers && event.printers.length > 0) {
          for (let i = 0; i < event.printers.length; i++) {
            const printer = event.printers[i];
            if (printer.ip) {
              await sendPrinterAndroid(
                getCashRegisterReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  ip: printer.ip,
                  port: Number(printer.port),
                  timeout: 400,
                  autoCut: true,
                  mmFeedPaper: 230,
                  printerDpi: 203,
                  printerWidthMM: 76,
                  printerNbrCharactersPerLine: 48,
                },
                true,
                false
              );
            } else if (printer.address || printer.macAddress) {
              await sendPrinterAndroid(
                getCashRegisterReceiptData(
                  order,
                  JSON.parse(restaurantData),
                  JSON.parse(restaurantPlaceData),
                  userInfo
                ),
                {
                  macAddress: (printer.address || printer.macAddress)
                },
                true,
                false
              );
            }
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      }

      return BackgroundFetch.BackgroundFetchResult.NewData;
    } catch (error) {
      ErrorReportingService.error('Failed to print cash register receipt', { error }, 'PRINT_CASH_REGISTER_TASK');
      ErrorReportingService.reportError(error, { source: 'PRINT_CASH_REGISTER_TASK', order: order?.orderCode });
      return BackgroundFetch.BackgroundFetchResult.Failed;
    }
  } catch (error) {
    ErrorReportingService.error('Error in PRINT_CASH_REGISTER_TASK', { error }, 'PRINT_CASH_REGISTER_TASK');
    ErrorReportingService.reportError(error, { source: 'PRINT_CASH_REGISTER_TASK', task: 'main' });
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Define kitchen printer task
TaskManager.defineTask(PRINT_KITCHEN_TASK, async ({ data }) => {
  try {
    const { order, items } = data;

    if (!order || !items) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Import Toast for notifications
    const Toast = require('react-native-toast-message').default;

    // Show printing in progress notification
    Toast.show({
      type: 'info',
      position: 'bottom',
      text1: i18n.t('printing_kitchen_progress'),
      visibilityTime: 10000, // 10 seconds or until dismissed
    });

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') {
      // Hide the notification if not on Android
      Toast.hide();
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    const userInfo = await getUserInfo();
    const restaurantData = await AsyncStorage.getItem("restaurant");

    // Import required functions
    const { sendPrinterAndroid } = require('./printerLibrary/printer');
    const { getBigTerminalData } = require('./printerLibrary/oldPrinter');

    // Print to kitchen printers
    const storedPrinterEvents = await AsyncStorage.getItem('printerEvents');
    const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

    if (printerEvents.length > 0) {
      const terminalEvents = printerEvents.filter(event => event.type === "terminal");
      // Products explicitly assigned to any terminal profile; the rest are
      // unassigned/new and are picked up by whichever profile is `catchAll`.
      const assignedProductIds = collectAssignedTerminalProductIds(terminalEvents);

      for (let eventIndex = 0; eventIndex < terminalEvents.length; eventIndex++) {
        const event = terminalEvents[eventIndex];

        if (event.printers && event.printers.length > 0) {
          for (let printerIndex = 0; printerIndex < event.printers.length; printerIndex++) {
            const printer = event.printers[printerIndex];

            // Route items: explicit assignment, legacy print-all (empty list),
            // or the catch-all fallback for unassigned/newly created products.
            const filteredItems = items.filter(item =>
              terminalEventPrintsProduct(event, (item.itemId || item.item?._id || item.item), assignedProductIds)
            );

            if (!filteredItems || filteredItems.length === 0) {
              console.log("No items for this printer event", { name: event.name, products: filteredItems.length });
              continue;
            }

            if (printer.ip) {
              for (let i = 0; i < (event.copy || 1); i++) {
                await sendPrinterAndroid(
                  getBigTerminalData(
                    {
                      ...order,
                      items: filteredItems
                    },
                    JSON.parse(restaurantData),
                    userInfo
                  ),
                  {
                    ip: printer.ip,
                    port: Number(printer.port),
                    timeout: 400,
                    autoCut: true,
                    mmFeedPaper: 230,
                    printerDpi: 203,
                    printerWidthMM: 80,
                    printerNbrCharactersPerLine: 42,
                  }
                );
                // Add a small delay between prints
                await new Promise(resolve => setTimeout(resolve, 50));
              }
            } else if (printer.address || printer.macAddress) {
              for (let i = 0; i < (event.copy || 1); i++) {
                await sendPrinterAndroid(
                  getBigTerminalData(
                    {
                      ...order,
                      items: filteredItems
                    },
                    JSON.parse(restaurantData),
                    userInfo
                  ),
                  {
                    macAddress: (printer.address || printer.macAddress),
                  }
                );
                // Add a small delay between prints
                await new Promise(resolve => setTimeout(resolve, 50));
              }
            }
          }
        }
      }
    }

    await AsyncStorage.setItem('kitchenPrinted', 'true');

    // Hide the notification when printing is complete
    Toast.hide();

    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background kitchen printing task error:", error);

    // Hide the notification and show error if printing fails
    Toast.hide();
    Toast.show({
      type: 'error',
      position: 'bottom',
      text1: i18n.t('kitchen_printing_failed'),
      text2: error.message,
      visibilityTime: 3000,
    });

    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger kitchen printer task
export const triggerKitchenPrinting = async (order, items, options = {}) => {
  try {
    // printedTargets: optional Set shared across retries of the same job.
    const { showAlert = true, printedTargets = null } = options;
    let failedCount = 0;
    await AsyncStorage.setItem('kitchenPrinted', 'false');

    const normalizeId = (value) => {
      if (!value) return '';
      if (typeof value === 'string') return value;
      if (typeof value === 'number') return String(value);
      if (typeof value === 'object') return value._id || value.id || value.$oid || '';
      return '';
    };

    const resolvePrintableProductId = (item = {}) => {
      return normalizeId(
        item?.itemId
        || item?.item?._id
        || item?.item?.id
        || item?.item
        || item?._id
        || item?.id
      );
    };

    const { Platform } = require('react-native');
    if (Platform.OS !== 'android') return false;

    try {
      // Direct implementation for kitchen printing
      const [userInfo, restaurantData, storedPrinterEvents] = await Promise.all([
        getUserInfo(),
        AsyncStorage.getItem("restaurant"),
        AsyncStorage.getItem('printerEvents'),
      ]);
      // Parse the restaurant document once, not once per printer/copy.
      const parsedRestaurantData = restaurantData ? JSON.parse(restaurantData) : {};

      const { sendPrinterAndroid } = require('./printerLibrary/printer');
      const { getBigTerminalData } = require('./printerLibrary/oldPrinter');

      const printerEvents = storedPrinterEvents ? JSON.parse(storedPrinterEvents) : [];

      if (printerEvents.length > 0) {
        const terminalEvents = printerEvents.filter(event => event.type === "terminal");
        // Products explicitly assigned to any terminal profile. Anything outside
        // this set is treated as unassigned/new (e.g. a freshly created food) and
        // is picked up by whichever profile is flagged `catchAll`.
        const assignedProductIds = collectAssignedTerminalProductIds(terminalEvents, normalizeId);

        for (let eventIndex = 0; eventIndex < terminalEvents.length; eventIndex++) {
          const event = terminalEvents[eventIndex];

          if (event.printers && event.printers.length > 0) {
            for (let printerIndex = 0; printerIndex < event.printers.length; printerIndex++) {
              const printer = event.printers[printerIndex];

              // Route items: explicit assignment, legacy print-all (empty list),
              // or the catch-all fallback for unassigned/newly created products.
              const filteredItems = items.filter((item) =>
                terminalEventPrintsProduct(event, resolvePrintableProductId(item), assignedProductIds, normalizeId)
              );

              if (!filteredItems || filteredItems.length === 0) {
                console.log("No items for this printer event", { name: event.name, products: filteredItems.length });
                continue;
              }

              const terminalPayload = getBigTerminalData(
                {
                  ...order,
                  items: filteredItems
                },
                parsedRestaurantData,
                userInfo
              );

              if (printer.ip || printer.address || printer.macAddress) {
                for (let i = 0; i < (event.copy || 1); i++) {
                  // Keep going past a dead printer; remember what printed so a
                  // retry of this job never duplicates a ticket.
                  const targetKey = `kitchen:${eventIndex}:${printerIndex}:${getPrinterTargetKey(printer)}:${i}`;
                  if (printedTargets?.has(targetKey)) {
                    continue;
                  }
                  try {
                    await printToTarget({
                      sendPrinterAndroid,
                      payload: terminalPayload,
                      printer,
                      widthMM: 80,
                      charactersPerLine: 42,
                      showAlert,
                    });
                    printedTargets?.add(targetKey);
                  } catch (targetError) {
                    failedCount += 1;
                    console.error('Kitchen printer failed:', targetKey, targetError?.message || targetError);
                  }
                  await new Promise(resolve => setTimeout(resolve, 50));
                }
              }
            }
          }
        }
      }

      // Food labels ("címke"): one sticker per ordered piece, routed by the
      // `label` printer profiles (explicit product assignment only). A dead
      // label printer is counted, never allowed to break the kitchen ticket.
      const labelResult = await printOrderLabels({
        order,
        items,
        printerEvents,
        sendPrinterAndroid,
        printedTargets,
      });
      failedCount += labelResult.failed;

      if (failedCount > 0) {
        return false;
      }

      await AsyncStorage.setItem('kitchenPrinted', 'true');
      return true;
    } catch (printError) {
      console.error('Failed to print kitchen receipt directly:', printError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger kitchen printing:', error);
    return false;
  }
};

// Check if kitchen has been printed
export const checkKitchenPrinted = async () => {
  const printed = await AsyncStorage.getItem('kitchenPrinted');
  return printed === 'true';
};


// Define fiscal printer task
TaskManager.defineTask(FISCAL_PRINTER_TASK, async ({ data }) => {
  try {
    const { orderData, operation } = data;

    if (!orderData || !operation) {
      return BackgroundFetch.BackgroundFetchResult.NoData;
    }

    // Import the FiscatPrinter module
    const { FiscatPrinter } = require('../paymentModules/FiscatFiscalModule');

    let result = false;

    // Execute the requested operation
    switch (operation) {
      case 'printReceipt':
        await FiscatPrinter.printReceipt(orderData);
        result = true;
        break;
      case 'printDeliveryInfo':
        await FiscatPrinter.printDeliveryInfo(orderData);
        result = true;
        break;
      case 'openDay':
        await FiscatPrinter.openDay();
        result = true;
        break;
      case 'closeDay':
        await FiscatPrinter.closeDay();
        result = true;
        break;
      default:
        console.error('Unknown fiscal printer operation:', operation);
        return BackgroundFetch.BackgroundFetchResult.Failed;
    }

    await AsyncStorage.setItem('fiscalPrinterOperationCompleted', 'true');
    return BackgroundFetch.BackgroundFetchResult.NewData;
  } catch (error) {
    console.error("Background fiscal printer task error:", error);
    return BackgroundFetch.BackgroundFetchResult.Failed;
  }
});

// Trigger fiscal printer task
export const triggerFiscalPrinter = async (orderData, operation) => {
  try {
    await AsyncStorage.setItem('fiscalPrinterOperationCompleted', 'false');

    try {
      // Direct implementation for fiscal printer
      const { FiscatPrinter } = require('../paymentModules/FiscatFiscalModule');

      let result = false;

      // Execute the requested operation directly
      switch (operation) {
        case 'printReceipt':
          await FiscatPrinter.printReceipt(orderData);
          result = true;
          break;
        case 'printDeliveryInfo':
          await FiscatPrinter.printDeliveryInfo(orderData);
          result = true;
          break;
        case 'openDay':
          await FiscatPrinter.openDay();
          result = true;
          break;
        case 'closeDay':
          await FiscatPrinter.closeDay();
          result = true;
          break;
        default:
          console.error('Unknown fiscal printer operation:', operation);
          return false;
      }

      await AsyncStorage.setItem('fiscalPrinterOperationCompleted', 'true');
      return result;
    } catch (opError) {
      console.error('Failed to execute fiscal printer operation directly:', opError);
      return false;
    }
  } catch (error) {
    console.error('Failed to trigger fiscal printer:', error);
    return false;
  }
};

// Check if fiscal printer operation has been completed
export const checkFiscalPrinterOperationCompleted = async () => {
  const completed = await AsyncStorage.getItem('fiscalPrinterOperationCompleted');
  return completed === 'true';
};
